/**
 * InboundDocumentsSection - Shows inbound documents linked to an order
 * Used in Order Detail page to display documents received from Inbound Hub
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { API_BASE } from '../lib/apiBase';
import { 
    Inbox,
    FileText,
    ExternalLink,
    CheckCircle,
    AlertTriangle,
    Clock,
    XCircle,
    Download,
    Eye
} from 'lucide-react';

const STATUS_CONFIG = {
    linked: { label: 'Linked', icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    needs_review: { label: 'Needs Review', icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-50' },
    pending: { label: 'Pending', icon: Clock, color: 'text-blue-600', bg: 'bg-blue-50' },
    failed: { label: 'Failed', icon: XCircle, color: 'text-red-600', bg: 'bg-red-50' },
};

const DOCUMENT_TYPE_LABELS = {
    ncb_clearance: 'NCB Clearance',
    invoice: 'Invoice',
    delivery_note: 'Delivery Note',
    production_confirmation: 'Production',
    artwork_approval: 'Artwork',
    shipping_notice: 'Shipping',
    other: 'Other',
};

// Status badge component - defined outside the main component
const StatusBadge = ({ status }) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    const Icon = config.icon;
    return (
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs ${config.bg} ${config.color}`}>
            <Icon className="h-3 w-3" />
        </span>
    );
};

export function InboundDocumentsSection({ orderId }) {
    const navigate = useNavigate();
    const [documents, setDocuments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (orderId) {
            fetchDocuments();
        }
    }, [orderId]);

    const fetchDocuments = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${API_BASE}/api/inbound-documents?order_id=${orderId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setDocuments(response.data || []);
        } catch (error) {
            console.error('Failed to fetch inbound documents:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short'
        });
    };

    if (isLoading) {
        return (
            <div className="text-center py-4 text-sm text-slate-400">
                Loading documents...
            </div>
        );
    }

    if (documents.length === 0) {
        return (
            <div className="text-center py-4 text-sm text-slate-400 border border-dashed rounded-lg">
                No inbound documents linked
            </div>
        );
    }

    const handleApplyNCB = async (doc) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${API_BASE}/api/inbound-documents/${doc.id}/apply-ncb`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (response.data.status === 'ncb_updated') {
                toast.success(`NCB status updated to "${response.data.applied_status}"`);
                // Refresh parent page to show updated NCB status
                window.location.reload();
            } else {
                toast.info(response.data.reason || 'No change made');
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to apply NCB status');
        }
    };

    return (
        <div className="space-y-2">
            {documents.map((doc) => (
                <div
                    key={doc.id}
                    className="flex items-center justify-between p-2 bg-background rounded-lg border border-border"
                >
                    <div className="flex items-center gap-3 min-w-0">
                        <StatusBadge status={doc.status} />
                        <div className="min-w-0">
                            <div className="flex items-center gap-2">
                                <span className="text-xs bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">
                                    {DOCUMENT_TYPE_LABELS[doc.document_type] || doc.document_type}
                                </span>
                                <span className="text-xs text-slate-400">
                                    {formatDate(doc.received_at)}
                                </span>
                            </div>
                            <p className="text-sm font-medium text-slate-700 truncate mt-0.5">
                                {doc.subject || doc.from_email || doc.external_id}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                        {doc.attachments && doc.attachments.length > 0 && (
                            <span className="text-xs text-slate-400 mr-2">
                                {doc.attachments.length} file{doc.attachments.length !== 1 ? 's' : ''}
                            </span>
                        )}
                        {/* Apply NCB button for NCB clearance documents */}
                        {doc.document_type === 'ncb_clearance' && doc.status === 'linked' && (
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2 text-xs text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                title="Apply NCB status from this document"
                                onClick={() => handleApplyNCB(doc)}
                            >
                                Apply NCB
                            </Button>
                        )}
                        {doc.inbound_hub_item_url && (
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                title="View in Inbound Hub"
                                onClick={() => window.open(doc.inbound_hub_item_url, '_blank')}
                            >
                                <ExternalLink className="h-3.5 w-3.5" />
                            </Button>
                        )}
                    </div>
                </div>
            ))}
            
            {/* Link to full Inbound Documents page */}
            <div className="text-center pt-2">
                <Button
                    variant="link"
                    size="sm"
                    className="text-xs text-slate-500"
                    onClick={() => navigate('/admin/inbound-documents')}
                >
                    View all inbound documents →
                </Button>
            </div>
        </div>
    );
}
